"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { Card, Col, Divider, Row, Tag } from "antd"
import { FC, Fragment, JSX, useMemo } from "react"
import { useAppSelector } from "@/lib/redux/hooks"
import dayjs from "dayjs"
import { parseJson } from "@/utils/safeParse/parseJson"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"

import { getValidDayjs } from "@/utils/safeParse/getValidDayjs"
import { Passenger } from "@/lib/redux/features/booking/type"
import { checkingOnlineFee } from "@/hooks/booking/useCheckinOnline"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import FlightDetailSegments from "@/components/flight/flight-detail/desktop/FlightDetailSegments"
import { groupSegments } from "@/utils/flight/groupSegments"
import { formatFlightPnrDisplay, getFlightPnrDisplayItems, getPnrStatusTagColor } from "@/utils/booking/getFlightPnrDisplayItems"
import { getBaggageDisplayLabel } from "@/utils/flight/getBaggageDisplayLabel"
import { getPassengerTitleI18nKey } from "@/utils/flight/getPassengerTitleI18nKey"

const FlightBooking: FC<{ showExpiredDay?: boolean }> = ({ showExpiredDay }) => {
  const order = useAppSelector(state => state.booking.order_detail)
  const countries = useAppSelector(state => state.booking.countries)
  const metaData = parseJson(order.data?.metaData) || []
  const firstFlight = metaData[0]
  const passengers = (firstFlight?.passengers || [])
  const purchasedBaggages = metaData.flatMap((booking: any) =>
    (booking.passengers || []).flatMap((passenger: any) => passenger.baggage || []),
  )
  const contact = (firstFlight?.contact || {})
  const isDesktop = useIsDesktop()
  const { getFormatCurrency } = useTETranslation()
  const { t } = useTranslation()

  const itinData = parseJson(order?.data?.itinData) || [];

  const totalAmount = parseFloat(order.data?.totalAmount || "0")
  const discountAmount = parseFloat(order.data?.discountAmount || "0")

  const flights = useMemo(() => {
    return (order.data?.tickets || []).reduce((arr: any[], ticket: any) => {
      const ticketFares = ticket?.metaData?.fareData || []
      for (let x = 0; x < ticketFares.length; x++) {
        const fareFlightRefs = ticketFares[x]?.flights || []
        const { flights: ticketFlights, ...ticketFare } = ticketFares[x]
        for (let i = 0; i < ticketFlights.length; i++) {
          const ticketFlight = ticketFlights[i];
          const fareFlightRef = fareFlightRefs.find(
            (fareFlight: any) => fareFlight.flightValue === ticketFlight.inventories[0].flightValue,
          )
          const isExisted = !!arr.length && arr.some(f => (
            f.inventories[0].flightValue === ticketFlight.inventories[0].flightValue
          ))
          if (!isExisted) {
            arr.push({
              ...ticketFlight,
              leg: ticketFlight.leg ?? fareFlightRef?.leg ?? 0,
              inventory: ticketFlight.inventories[0],
              fareData: ticketFare,
            })
          }
        }

      }
      return arr
    }, [])
  }, [order.data?.tickets])

  const groupSegs = useMemo(() => {
    const flights = []
    for (let i = 0; i < itinData.length; i++) {
      const segment: any = itinData[i];
      if (segment.id === 0) {
        flights.push(groupSegments(segment, itinData))
      }
    }
    return flights
  }, [itinData])

  const itineraryGroups = useMemo(() => {
    return groupSegs.map((segments: any[]) => {
      const leg = Number(segments[0]?.leg) || 0
      const sourceFlightForSegment = (segment: any) =>
        flights.find((flight: any) =>
          Number(flight.leg) === leg &&
          flight.segments?.some(
            (flightSegment: any) =>
              flightSegment.flightNumber === segment.flightNumber,
          ),
        )
      const sourceFlight = segments
        .map(sourceFlightForSegment)
        .find(Boolean) || flights.find((flight: any) => Number(flight.leg) === leg)
      const amenities = segments.map((segment: any) => {
        if (segment.amenities) return segment.amenities
        const matchingFlight = sourceFlightForSegment(segment)
        const matchingSegmentIndex = matchingFlight?.segments?.findIndex(
          (flightSegment: any) =>
            flightSegment.flightNumber === segment.flightNumber,
        )
        return matchingSegmentIndex >= 0
          ? matchingFlight?.amenities?.[matchingSegmentIndex]
          : undefined
      })

      return {
        leg,
        segments,
        flights: [{ ...sourceFlight, leg, segments, amenities }],
      }
    })
  }, [flights, groupSegs])

  const expiryDate = (order?.data?.tickets || [])[0]?.expiryDate || order?.data?.expiryDate
  const isDomestic = (order.data?.tickets || []).every(ticket => ticket.metaData.isDomestic)
  const hasReturn = itineraryGroups.some(({ leg }) => leg > 0)
  const pnrItems = useMemo(() => {
    return getFlightPnrDisplayItems({
      pnrNumber: order?.data?.pnrNumber,
      tickets: order?.data?.tickets,
      orderStatus: order?.data?.orderStatus,
      bookingStatus: order?.data?.bookingStatus,
      ticketStatus: order?.data?.ticketStatus
    })
  }, [order?.data?.pnrNumber, order?.data?.tickets, order?.data?.orderStatus, order?.data?.bookingStatus, order?.data?.ticketStatus])
  const renderInforPassenger = (passenger?: any): JSX.Element => {
    const birthday = dayjs(passenger?.birthday)
    const nationality = countries.find((c) => c.code === passenger?.nationality)

    return (
      <>
        <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
          <Col xs={24} sm={{ flex: "20rem" }}>
            <Text>{t("common.fullname")}:</Text>
          </Col>
          <Col>
            {`(${t(getPassengerTitleI18nKey(!!passenger?.gender, passenger?.paxType))}) ${passenger?.lastName ? `${passenger?.lastName} ` : ''}${passenger?.firstName || ''}`}
          </Col>
        </Row>
        <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
          <Col xs={24} sm={{ flex: "20rem" }}>
            <Text>{t("common.dob")}:</Text>
          </Col>
          <Col>
            {passenger?.birthday ? birthday.format("DD/MM/YYYY") : `--/--/--`}
          </Col>
        </Row>

        <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
          <Col xs={24} sm={{ flex: "20rem" }}>
            <Text>{t("common.nationality")}:</Text>
          </Col>
          <Col>
            {nationality?.name || ''}
          </Col>
        </Row>
      </>
    )
  }

  return (
    <>
      <Row gutter={[0, 15]} style={{ marginBlock: '1.5rem' }}>
        <Col span={24}>
          <Card
            size='small'
            className="booking__result--bookingcode"
          >
            <Row justify='space-between' align='middle'>
              <Col>
                <Text>{t("booking.ticket_flight_code")}</Text>
              </Col>
              <Col>
                <Row gutter={[10, 10]} justify='end'>
                  {pnrItems.map((item) => (
                    <Col key={`${item.pnr}_${item.airline || "na"}`}>
                      <Row align='middle' gutter={[8, 8]}>
                        <Col>
                          <Text copyable={{ text: item.pnr }} strong className="booking__result--bookingcode-btncopy">{formatFlightPnrDisplay(item)}</Text>
                        </Col>
                        <Col>
                          <Tag color={getPnrStatusTagColor(item.status)}>
                            {t(`booking.pnr_status_${item.status}`)}
                          </Tag>
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
        {order.data?.bookingStatus !== 'ACTIVE' && showExpiredDay && expiryDate && (
          <Col span={24}>
            <Card
              size='small'
              className="booking__result--bookingcode"
            >
              <Row justify='space-between' align='middle'>
                <Col>
                  <Text>Hạn giữ chỗ</Text>
                </Col>
                <Col>
                  <Text type='danger' strong>{getValidDayjs(expiryDate).format('DD/MM/YYYY HH:mm A')}</Text>
                </Col>
              </Row>
            </Card>
          </Col>
        )}

      </Row>
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("booking.contact_info")}
        </Title>
      </Row>
      <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
        <Col xs={24} sm={{ flex: "20rem" }}>
          <Text>{t("common.fullname")}:</Text>
        </Col>
        <Col>
          {`(${contact?.gender ? t("common.mr") : t("common.mrs")}) ${contact?.lastName ? `${contact?.lastName} ` : ''}${contact?.firstName || ''}`}
        </Col>
      </Row>
      <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
        <Col xs={24} sm={{ flex: "20rem" }}>
          <Text>{t("common.phonenumber")}: </Text>
        </Col>
        <Col>
          {`(${contact?.area || ''}) ${contact?.phone || ''}`}
        </Col>
      </Row>

      <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
        <Col xs={24} sm={{ flex: "20rem" }}>
          <Text>Email: </Text>
        </Col>
        <Col>
          {contact?.email || ''}
        </Col>
      </Row>
      <Divider style={{ marginBlock: '1.5rem' }} />
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("booking.pass_info")}
        </Title>
      </Row>
      <Row gutter={[15, 15]}>
        {passengers.map((pass: any, index: number) => {
          return (
            <Col xs={24} lg={12} xl={12}>
              <Row>
                <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                  {`${t("booking.passenger")} ${index + 1}`}
                </Title>
              </Row>
              {renderInforPassenger(pass)}
            </Col>
          )
        })}
      </Row>
      <Row>
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("booking.extra_service_title")}
            </Title>
          </Row>
          {order.data?.isCheckin && (
            <>
              <Row>
                <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                  Checkin online
                </Title>
              </Row>
              <Row style={{ marginBottom: '1rem' }}>
                <Col>
                  <Text strong>
                    {getFormatCurrency(
                      checkingOnlineFee *
                      passengers.filter((p: Passenger) => p.paxType !== 'INF').length *
                      Math.max(metaData.length, 1)
                    )}
                  </Text>
                </Col>
              </Row>
            </>
          )}
          {metaData.map((flight: any, idx: number) => {
            const key = flight.fareData.map((fare: any) => fare.flights.map((f: any) => f.flightValue).join('_')).join('_')
            const isReturn = flight.fareData.some((fare: any) => fare.flights.some((f: any) => f.leg > 0))

            const seats = flight.passengers.filter((pass: any) => pass.seats.length > 0).map((pass: Passenger) => {
              return (
                <Row style={{ marginBottom: '1rem' }}>
                  <Col>
                    <Row>
                      {`${pass.lastName || ''} ${pass.firstName || ''}`} - {pass.seats.map((s: any) => s.seatNumber).join(', ')}
                    </Row>
                    <Row align='middle'>
                      <Text strong>
                        {getFormatCurrency(pass.seats.reduce((t: any, b: any) => b.price + t, 0))}
                      </Text>
                    </Row>
                  </Col>
                </Row>
              )

            }, [])

            const baggages = flight.passengers.filter((pass: Passenger) => pass.baggage.length > 0).map((pass: Passenger) => {
              return (
                <Row style={{ marginBottom: '1rem' }}>
                  <Col>
                    <Row>
                      {`${pass.lastName || ''} ${pass.firstName || ''}`} - {pass.baggage.map((b: any) => getBaggageDisplayLabel(b)).join(', ')}
                    </Row>
                    <Row align='middle'>
                      <Text strong>
                        {getFormatCurrency(pass.baggage.reduce((t: any, b: any) => b.price + t, 0))}
                      </Text>
                    </Row>
                  </Col>
                </Row>
              )

            }, [])

            if (!seats.length && !baggages?.length) {
              return <></>
            }

            return (
              <Fragment
                key={key}
              >
                <Row className="passenger__order--flight-top">
                  <Col className="passenger__order--flight-top--box">
                    <Text strong className="te-text-small">
                      {hasReturn || flights.length === 1 ? `${isReturn ? t("flight.return_way") : t("flight.departure_way")}` : `${t("flight.flight_short")} ${idx + 1}`}
                    </Text>
                  </Col>
                </Row>
                {!!seats.length && (<Row>
                  <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                    {t("flight.seat")}
                  </Title>
                </Row>)}
                {seats}
                {!!baggages.length && (<Row>
                  <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                    {t("flight.allowance_baggage")}
                  </Title>
                </Row>)}
                {baggages}
              </Fragment>
            )
          })}
          <Row>

          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("booking.payment_info")}
            </Title>
          </Row>

          <Row justify='space-between' align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem', maxWidth: '40rem' }}>
            <Col sm={{ flex: "20rem" }}>
              <Text>{t("common.discount")}: </Text>
            </Col>
            <Col>
              {getFormatCurrency(discountAmount)}
            </Col>
          </Row>

          <Row justify='space-between' align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem', maxWidth: '40rem' }}>
            <Col sm={{ flex: "20rem" }}>
              <Text strong>{t("booking.total_amount")}: </Text>
            </Col>
            <Col>
              <Text type='danger' strong>{getFormatCurrency(totalAmount)}</Text>
            </Col>
          </Row>

        </Col>
      </Row>
      <Row className="booking__result--flights">
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("booking.travel_itinerary")}
            </Title>
          </Row>
          <Row gutter={[16, 16]}>
            {itineraryGroups.map(({ leg, flights: legFlights, segments }, idx) => (
              <Col
                key={`${leg}-${segments[0]?.flightNumber || idx}`}
                span={hasReturn && isDesktop ? 12 : 24}
              >
                <Row className="passenger__order--flight-top">
                  <Col className="passenger__order--flight-top--box">
                    <Text strong className="te-text-small">
                      {hasReturn || itineraryGroups.length === 1
                        ? leg > 0 ? t("flight.return_way") : t("flight.departure_way")
                        : `${t("flight.flight_short")} ${idx + 1}`}
                    </Text>
                  </Col>
                </Row>
                {legFlights.map((flight: any, flightIndex: number) => (
                  <FlightDetailSegments
                    key={`${leg}-${flight.id || flight.flightValue || flightIndex}`}
                    group={false}
                    style={{ boxShadow: 'none' }}
                    isDomestic={isDomestic}
                    shownDetail
                    baggages={purchasedBaggages}
                    flight={flight}
                  />
                ))}
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </>
  )
}

export default FlightBooking